/**
 * product-box-dynamic.js
 * -----------------------------------------------------------------------
 * Makes the "landing_page_product" block dynamic:
 *   - Pulls live variant data from Shopify's native
 *     /products/{handle}.js endpoint.
 *   - Updates every price / variant-id node in the block when the
 *     shopper picks a pack tier.
 *   - Adds to cart via /cart/add.js and opens the theme's existing
 *     <cart-drawer> using its own public getSectionsToRender() /
 *     renderContents() methods (defined in cart-drawer.js), so it stays
 *     in sync with the rest of the theme instead of re-implementing it.
 *
 * ASSUMPTIONS (check these against your setup):
 *   1. Each pack tier is a *separate variant* whose price already
 *      reflects the bundle (e.g. "Buy 2 Get 1 Free" = one variant
 *      priced at 2x). Cart-add quantity is therefore 1 per tier.
 *      If your variants instead represent a single bottle and the
 *      "pack" is achieved by adding multiple units, change
 *      CART_QUANTITY_MODE below to 'units'.
 *   2. The 3 quantity_item / cc-offer-card slots map to the first 3
 *      variants on the product, in order.
 *
 * Install: upload as an theme asset and load it after the block
 * renders, e.g. in theme.liquid before </body>:
 *   {{ 'product-box-dynamic.js' | asset_url | script_tag }}
 * or drop it in a <script src="..."> tag directly on the template.
 * -----------------------------------------------------------------------
 */
(function () {
  'use strict';

  const CART_QUANTITY_MODE = 'pack'; // 'pack' = 1 per tier, 'units' = quantity_item's [quantity] attr

  /* ----------------------------------------------------------------
   * Money formatting
   * ---------------------------------------------------------------- */
  function shopCurrency() {
    try {
      return (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
    } catch (e) {
      return 'USD';
    }
  }

  function formatMoney(cents) {
    if (cents === null || cents === undefined) return '';
    // Prefer the theme's own money formatter if it's exposed globally.
    try {
      if (window.theme && typeof window.theme.formatMoney === 'function') {
        return window.theme.formatMoney(cents);
      }
    } catch (e) { /* fall through */ }
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: shopCurrency(),
      }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  /* ----------------------------------------------------------------
   * Product data
   * ---------------------------------------------------------------- */
  function getHandle() {
    const m = window.location.pathname.match(/\/products\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  }

  async function fetchProduct() {
    const handle = getHandle();
    if (!handle) throw new Error('product-box-dynamic: could not read product handle from URL');
    const res = await fetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('product-box-dynamic: failed to load product JSON (' + res.status + ')');
    return res.json();
  }

  /* ----------------------------------------------------------------
   * State
   * ---------------------------------------------------------------- */
  const state = {
    product: null,
    tiers: [],          // [{ variant, badgePct }] length up to 3
    activeTierIndex: 0,
  };

  function tierPricing(tier) {
    const v = tier.variant;
    const oneTime = {
      price: v.price,
      compareAt: v.compare_at_price && v.compare_at_price > v.price ? v.compare_at_price : null,
    };
    return { oneTime };
  }

  /* ----------------------------------------------------------------
   * DOM helpers
   * ---------------------------------------------------------------- */
  function showOnlyByAttr(nodeList, attr, value) {
    nodeList.forEach((el) => {
      el.style.display = el.getAttribute(attr) === String(value) ? '' : 'none';
    });
  }

  function priceHTML(price, compareAt) {
    let html = formatMoney(price);
    if (compareAt) html += '<s>' + formatMoney(compareAt) + '</s>';
    return html;
  }

  /* ----------------------------------------------------------------
   * Render: pack tier cards (.quantity_flex_item)
   * ---------------------------------------------------------------- */
  function renderQuantityCards() {
    state.tiers.forEach((tier, i) => {
      const tierNum = i + 1;
      const item = document.querySelector(`.quantity_item[data_tab="${tierNum}"]`);
      if (!item) return;

      item.setAttribute('variant-id', tier.variant.id);
      item.classList.toggle('active', i === state.activeTierIndex);

      const { oneTime } = tierPricing(tier);

      const priceEl = item.parentElement.querySelector('.quantity_price');
      if (priceEl) priceEl.innerHTML = priceHTML(oneTime.price, oneTime.compareAt);

      // Recompute discount badge off the one-time price.
      const badge = item.querySelector('.quantity_badge');
      if (badge && oneTime.compareAt) {
        const pct = Math.round((1 - oneTime.price / oneTime.compareAt) * 100);
        badge.textContent = pct + '% OFF';
        badge.style.display = '';
      } else if (badge) {
        badge.style.display = 'none';
      }
    });
  }

  /* ----------------------------------------------------------------
   * Render: main Add to Cart button
   * ---------------------------------------------------------------- */
  function renderAtcButton() {
    const btn = document.querySelector('.custom_checkout_button');
    if (!btn) return;

    const tier = state.tiers[state.activeTierIndex];
    if (!tier) return;

    const qty = CART_QUANTITY_MODE === 'units' ? (tier.variant.quantity_rule?.min || 1) : 1;
    btn.setAttribute('quantity', qty);
    btn.setAttribute('variant_id', tier.variant.id);

    const available = tier.variant.available;
    btn.style.pointerEvents = available ? '' : 'none';
    btn.style.opacity = available ? '' : '0.5';

    state.tiers.forEach((t, i) => {
      const tierNum = i + 1;
      const { oneTime } = tierPricing(t);

      const priceSpan = btn.querySelector(`.product_price_custom.price_trigger[data_tab="${tierNum}"]`);
      if (priceSpan) {
        priceSpan.innerHTML = `Add To Cart  – ${priceHTML(oneTime.price, oneTime.compareAt)}`;
      }
    });

    // Only the active tier's price label is visible on the button.
    showOnlyByAttr(
      btn.querySelectorAll('.product_price_custom.price_trigger[data_tab]'),
      'data_tab',
      String(state.activeTierIndex + 1)
    );

    if (!available) {
      const label = btn.querySelector(`.product_price_custom.price_trigger[data_tab="${state.activeTierIndex + 1}"]`);
      if (label) label.textContent = 'Sold Out';
    }
  }

  /* ----------------------------------------------------------------
   * Render: hidden A/B "offer cards" block (kept correct in case it's
   * ever toggled on; the block's own inline script re-uses these
   * data-* attributes when the cards are clicked).
   * ---------------------------------------------------------------- */
  function renderOfferCards() {
    const cards = document.querySelectorAll('.cc-offer-card');
    cards.forEach((card, i) => {
      const tier = state.tiers[i];
      if (!tier) return;
      const { oneTime } = tierPricing(tier);

      card.setAttribute('data-variant-id', tier.variant.id);
      card.setAttribute('data-regular-price-formatted', formatMoney(oneTime.price));
      card.setAttribute('data-variant-compare-price-formatted', formatMoney(oneTime.compareAt || 0));
      card.setAttribute('data-one-time-save-price-formatted', formatMoney(oneTime.compareAt ? oneTime.compareAt - oneTime.price : 0));
      card.classList.toggle('cc-offer-card--active', i === state.activeTierIndex);

      const priceEl = card.querySelector('.cc-offer-card__price');
      const compareEl = card.querySelector('.cc-offer-card__compare-price');
      if (priceEl) priceEl.textContent = formatMoney(oneTime.price);
      if (compareEl) compareEl.textContent = oneTime.compareAt ? formatMoney(oneTime.compareAt) : '';
    });
  }

  /* ----------------------------------------------------------------
   * Render: hidden variants_id <select> (kept in sync for form
   * semantics / analytics scripts that might read it)
   * ---------------------------------------------------------------- */
  function renderVariantSelect() {
    const select = document.querySelector('select[name="variants_id"]');
    if (!select) return;
    select.innerHTML = '';
    state.tiers.forEach((tier, i) => {
      const opt = document.createElement('option');
      opt.value = tier.variant.id;
      opt.textContent = tier.variant.title;
      if (i === state.activeTierIndex) opt.selected = true;
      select.appendChild(opt);
    });
  }

  function renderAll() {
    renderQuantityCards();
    renderAtcButton();
    renderOfferCards();
    renderVariantSelect();
  }

  /* ----------------------------------------------------------------
   * Event wiring
   * ---------------------------------------------------------------- */
  function wireTierSelection() {
    document.querySelectorAll('.quantity_item[data_tab]').forEach((item, i) => {
      item.addEventListener('click', () => {
        state.activeTierIndex = i;
        renderAll();
      });
    });
  }

  /* ----------------------------------------------------------------
   * Add to cart + open cart drawer
   * ---------------------------------------------------------------- */
  function showAtcError(message) {
    const btn = document.querySelector('.custom_checkout_button');
    if (!btn) return;
    let err = btn.parentElement.querySelector('.pbd-atc-error');
    if (!err) {
      err = document.createElement('div');
      err.className = 'pbd-atc-error';
      err.style.cssText = 'color:#c0392b;font-size:13px;text-align:center;margin-top:8px;';
      btn.parentElement.appendChild(err);
    }
    err.textContent = message;
    setTimeout(() => { err.textContent = ''; }, 4000);
  }

  async function addToCart() {
    const tier = state.tiers[state.activeTierIndex];
    if (!tier || !tier.variant.available) return;

    const btn = document.querySelector('.custom_checkout_button');
    if (btn) btn.style.pointerEvents = 'none';

    const qty = CART_QUANTITY_MODE === 'units' ? (tier.variant.quantity_rule?.min || 1) : 1;
    const item = { id: tier.variant.id, quantity: qty };

    const cartDrawer = document.querySelector('cart-drawer');
    let sections = ['cart-drawer', 'cart-icon-bubble'];
    try {
      if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
        sections = cartDrawer.getSectionsToRender().map((s) => s.id);
      }
    } catch (e) { /* keep fallback sections */ }

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [item],
          sections,
          sections_url: window.location.pathname,
        }),
      });
      const data = await res.json();

      if (!res.ok || data.status) {
        throw new Error(data.description || data.message || 'Could not add this item to the cart.');
      }

      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        // renderContents() only strips is-empty from .drawer__inner, which
        // never actually carries that class in this theme's markup — the
        // real is-empty class lives on the <cart-drawer> element itself, so
        // it must be cleared here too or CSS keeps showing the empty-state
        // view (blank drawer) on every subsequent open.
        cartDrawer.classList.remove('is-empty');
        cartDrawer.renderContents(data);
      } else {
        window.location.href = window.Shopify?.routes?.root ? window.Shopify.routes.root + 'cart' : '/cart';
      }
    } catch (err) {
      showAtcError(err.message || 'Something went wrong adding this to your cart.');
    } finally {
      if (btn) btn.style.pointerEvents = '';
    }
  }

  function wireAddToCart() {
    const btn = document.querySelector('.custom_checkout_button');
    if (!btn) return;
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      addToCart();
    });
  }

  /* ----------------------------------------------------------------
   * Init
   * ---------------------------------------------------------------- */
  function buildState(product) {
    state.product = product;
    state.tiers = (product.variants || []).slice(0, 3).map((variant) => ({ variant }));

    // Preserve whatever the static markup had pre-selected, by position.
    const initialTierEl = document.querySelector('.quantity_item.active[data_tab]');
    state.activeTierIndex = initialTierEl ? Math.max(0, parseInt(initialTierEl.getAttribute('data_tab'), 10) - 1) : 0;
    if (state.activeTierIndex >= state.tiers.length) state.activeTierIndex = 0;
  }

  async function init() {
    try {
      const product = await fetchProduct();
      buildState(product);
      renderAll();
      wireTierSelection();
      wireAddToCart();
    } catch (err) {
      console.error('product-box-dynamic:', err);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
